import { useMemo, useState } from 'react';
import { Star, MoreHorizontal, Search } from 'lucide-react';
import clsx from 'clsx';
import { formatDate, getServiceCallListCustomerName } from '@shared/utils';
import { CHECKLIST_STEPS_BY_MAILBOX } from './invoicingConstants';

const computeChecklistProgress = (call, mailbox) => {
  const steps = CHECKLIST_STEPS_BY_MAILBOX[mailbox] || [];
  const checklist = call.billingChecklist || {};
  let done = 0;
  steps.forEach((step) => {
    if (step.key === 'invoiceNumber') {
      if (call.invoiceNumber) done += 1;
    } else if (step.key === 'complete') {
      if (call.isFinalized) done += 1;
    } else if (checklist[step.key]) {
      done += 1;
    }
  });
  return { done, total: steps.length };
};

function MoveToFolderMenu({ call, manualFolders, currentFolderId, onMoveToFolder }) {
  const [open, setOpen] = useState(false);
  const targets = manualFolders.filter((f) => f.id !== currentFolderId);
  if (targets.length === 0) return null;

  return (
    <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded"
        title="Move to folder"
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-6 z-20 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1">
            <div className="px-2.5 py-1 text-2xs font-semibold text-gray-400 uppercase">Move to</div>
            {targets.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => {
                  onMoveToFolder(call.id, folder.id);
                  setOpen(false);
                }}
                className="w-full text-left px-2.5 py-1.5 text-xs text-gray-700 hover:bg-surface-hover"
              >
                {folder.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function InvoiceMessageList({
  mailbox,
  folderName,
  items,
  isLoading,
  selectedId,
  onSelect,
  onToggleStar,
  manualFolders = [],
  currentFolderId,
  onMoveToFolder,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  onBulkMarkQuickBooks,
  qboConnected = false,
  onBulkFinalize,
  bulkBusy = false,
  totalCount,
  hasMore = false,
  onLoadMore,
  loadingMore = false
}) {
  const [search, setSearch] = useState('');
  const [starredOnly, setStarredOnly] = useState(false);

  const filteredItems = useMemo(() => {
    let result = items;
    if (starredOnly) result = result.filter((call) => call.isStarred);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((call) => {
        const name = getServiceCallListCustomerName(call).toLowerCase();
        const invoice = (call.invoiceNumber || '').toLowerCase();
        const tech = call.technician ? `${call.technician.firstName} ${call.technician.lastName}`.toLowerCase() : '';
        return name.includes(q) || invoice.includes(q) || tech.includes(q);
      });
    }
    return result;
  }, [items, search, starredOnly]);

  const visibleIds = filteredItems.map((c) => c.id);
  const selectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedCount === visibleIds.length;
  const displayCount = totalCount != null ? totalCount : items.length;

  return (
    <div className="w-80 flex-shrink-0 border-r border-surface-border bg-surface flex flex-col" data-tour-id="invoicing-message-list">
      <div className="hub-dock-header">
        <span className="hub-dock-title">{folderName}</span>
        <span className="text-2xs tabular-nums font-semibold text-gray-600">
          {items.length}{totalCount != null && totalCount !== items.length ? ` / ${displayCount}` : ''}
        </span>
      </div>
      <div className="px-2.5 py-2 border-b border-surface-border flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search this folder..."
            className="input text-xs w-full pl-7"
          />
        </div>
        <button
          type="button"
          onClick={() => setStarredOnly((v) => !v)}
          title={starredOnly ? 'Showing starred only' : 'Show starred only'}
          className={clsx(
            'flex-shrink-0 p-1.5 rounded border transition-colors',
            starredOnly ? 'border-amber-300 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
          )}
        >
          <Star className={clsx('h-3.5 w-3.5', starredOnly ? 'fill-amber-400 text-amber-400' : 'text-gray-400')} />
        </button>
      </div>

      {visibleIds.length > 0 && (
        <div className="px-2.5 py-1.5 border-b border-surface-border flex items-center gap-2 flex-wrap bg-surface-secondary">
          <label className="inline-flex items-center gap-1.5 text-2xs text-gray-600">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={() => onToggleSelectAll?.(visibleIds)}
              className="rounded border-gray-300"
            />
            Select
          </label>
          {selectedCount > 0 && (
            <>
              <span className="text-2xs text-gray-500">{selectedCount} selected</span>
              <button
                type="button"
                className="text-2xs text-primary-700 hover:underline disabled:opacity-50"
                disabled={bulkBusy}
                onClick={onBulkMarkQuickBooks}
                title={qboConnected && mailbox === 'customers' ? 'Push selected calls to QuickBooks' : 'Mark selected calls as added to QuickBooks'}
              >
                {qboConnected && mailbox === 'customers' ? 'Send to QB' : 'Mark QB'}
              </button>
              <button
                type="button"
                className="text-2xs text-primary-700 hover:underline disabled:opacity-50"
                disabled={bulkBusy}
                onClick={onBulkFinalize}
              >
                Finalize
              </button>
            </>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 text-xs text-gray-500">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-4 text-xs text-gray-500 text-center">Nothing here</div>
        ) : (
          <ul>
            {filteredItems.map((call) => {
              const active = call.id === selectedId;
              const progress = computeChecklistProgress(call, mailbox);
              const checked = selectedIds.has(call.id);
              return (
                <li key={call.id}>
                  <div
                    className={clsx(
                      'w-full text-left px-3 py-2.5 border-b border-surface-border transition-colors group flex gap-2',
                      active ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'hover:bg-surface-hover'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleSelect?.(call.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 rounded border-gray-300 flex-shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => onSelect(call.id)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleStar(call.id, !call.isStarred);
                          }}
                          className="flex-shrink-0"
                        >
                          <Star
                            className={clsx(
                              'h-3.5 w-3.5',
                              call.isStarred ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-300'
                            )}
                          />
                        </span>
                        <span className="text-xs font-medium text-gray-900 truncate flex-1">
                          {getServiceCallListCustomerName(call)}
                        </span>
                        {!call.invoiceNumber && (
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" title="No invoice #" />
                        )}
                        <MoveToFolderMenu
                          call={call}
                          manualFolders={manualFolders}
                          currentFolderId={currentFolderId}
                          onMoveToFolder={onMoveToFolder}
                        />
                      </div>
                      <div className="text-2xs text-gray-500 mt-0.5 pl-5">
                        {call.technician ? `${call.technician.firstName} ${call.technician.lastName}` : '—'}
                        {call.completedDate && ` · ${formatDate(call.completedDate)}`}
                      </div>
                      <div className="flex items-center gap-2 pl-5 mt-1">
                        {call.invoiceNumber && (
                          <span className="text-2xs text-gray-600">#{call.invoiceNumber}</span>
                        )}
                        <span className="text-2xs text-gray-400">
                          {progress.done}/{progress.total} steps
                        </span>
                      </div>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {hasMore && (
          <div className="p-2 border-t border-surface-border">
            <button
              type="button"
              className="btn-secondary btn-sm w-full"
              disabled={loadingMore}
              onClick={onLoadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
